import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Permissions } from '@/auth/permissions';
import { useHasPermission } from '@/auth/RequirePermission';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { EstimateActs } from '@/components/estimates/EstimateActs';
import { EstimateBuilder } from '@/components/estimates/EstimateBuilder';
import { EstimateView } from '@/components/estimates/EstimateView';
import { EstimateNotesView } from '@/components/estimates/EstimateNotes';
import { CustomerLink, LINK_STATUSES } from '@/components/estimates/CustomerLink';
import { CustomerAnswer } from '@/components/estimates/CustomerAnswer';
import { ConvertToJob } from '@/components/estimates/ConvertToJob';
import { formatDate, formatDateOnly } from '@/lib/format';
import { NotFound } from '@/routes/NotFound';
import { useJob } from '@/routes/jobs/useJobDetail';
import { useTechnicianProfileByUser } from '@/routes/technicians/useTechnicianProfile';
import { useAccount } from '@/api/useAccount';
import { AccountLetterhead } from '@/components/company/AccountLetterhead';
import { DocumentTerms } from '@/components/company/DocumentTerms';
import {
  EstimateForbiddenError,
  EstimateNotFoundError,
  useEstimate,
} from '@/routes/estimates/useEstimates';

/**
 * One estimate (M2-06): the builder while it is a draft, the frozen document
 * once it is sent, and — for the office — the acts that move it along.
 *
 * <b>A technician reaches this for an estimate on a job they are on</b>
 * (`estimates.build`, narrowed by the API). A 403 is that narrowing working
 * — the same "not yours" message the job page gives, not the app-wide guard,
 * because it denies one record, not the section.
 */
export function EstimateDetail() {
  const { t } = useTranslation();
  const { estimateId } = useParams<{ estimateId: string }>();
  const hasPermission = useHasPermission();
  const isOffice = hasPermission(Permissions.manageCustomers);
  const estimate = useEstimate(estimateId);
  const account = useAccount();
  const timeZone = account.data?.timezone;

  // The labour rate (Kyle, 2026-09-26): the job lead's billable rate, for
  // Owner and Admin only — M1-37 keeps rates from everyone without
  // users.manage, the technician included, and the API blanks them anyway.
  const canSeeRates = hasPermission(Permissions.manageUsers);
  // Only fetched for the rate, so only for someone who may see it.
  const job = useJob(canSeeRates ? (estimate.data?.jobId ?? undefined) : undefined);
  const leadId = job.data?.job.assignedUserId ?? undefined;
  const leadProfile = useTechnicianProfileByUser(leadId, canSeeRates);
  const rate = leadProfile.data?.billableRateHourly;
  const hourlyRate = canSeeRates && rate != null ? Number(rate) : null;

  const [draftSettled, setDraftSettled] = useState(true);
  const onSettledChange = useCallback((settled: boolean) => setDraftSettled(settled), []);
  const [announcement, setAnnouncement] = useState('');

  if (estimate.error instanceof EstimateNotFoundError) {
    return <NotFound />;
  }

  if (estimate.isPending) {
    return <LoadingState label={t('estimates.detail.loading')} />;
  }

  if (estimate.error instanceof EstimateForbiddenError) {
    return (
      <ErrorState
        title={t('estimates.detail.notPermittedTitle')}
        description={t('estimates.detail.notPermittedDescription')}
      />
    );
  }

  if (estimate.isError) {
    return (
      <ErrorState
        title={t('estimates.detail.loadErrorTitle')}
        description={t('estimates.detail.loadErrorDescription')}
        onRetry={() => void estimate.refetch()}
      />
    );
  }

  const e = estimate.data;
  const number = Number(e.number);
  const revision = Number(e.revision);
  // On the SHOP's clock, not the viewer's (web/CONVENTIONS.md's "one clock" rule):
  // a send at 9pm in Kansas City is still that day to the office.
  const on = (value: string | null) =>
    value ? formatDate(value, { dateStyle: 'medium', ...(timeZone ? { timeZone } : {}) }) : '';

  return (
    <section className="space-y-6">
      {/* M2-19 (Q5 = A): the document carries the company it is from. */}
      <AccountLetterhead />
      <div className="space-y-1">
        <Link
          to="/estimates"
          className="text-sm text-[var(--color-muted-foreground)] hover:underline"
        >
          {t('estimates.detail.back')}
        </Link>
        <h1 className="text-xl font-semibold">
          {revision > 1
            ? t('estimates.numberedRevision', { number, revision })
            : t('estimates.numbered', { number })}
        </h1>
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span
            className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs font-medium"
            data-testid="estimate-status"
          >
            {t(`estimates.status.${e.status}`, { defaultValue: e.status })}
          </span>
          {isOffice ? (
            <Link to={`/customers/${e.customerId}`} className="hover:underline">
              {e.customerName}
            </Link>
          ) : (
            <span>{e.customerName}</span>
          )}
          {/* M2-09 follow-up: an estimate drafted on a finished job and
              worked on a new one links to BOTH, each saying which it is.
              Otherwise one job: a converted estimate's work is on the job it
              names (a job-less estimate's new job is written to both). */}
          {e.jobId && e.convertedJobId && e.convertedJobId !== e.jobId ? (
            <>
              <Link to={`/jobs/${e.jobId}`} className="hover:underline">
                {t('estimates.detail.viewDraftedJob')}
              </Link>
              <Link to={`/jobs/${e.convertedJobId}`} className="hover:underline">
                {t('estimates.detail.viewWorkJob')}
              </Link>
            </>
          ) : e.jobId ? (
            <Link to={`/jobs/${e.jobId}`} className="hover:underline">
              {t('estimates.detail.viewJob')}
            </Link>
          ) : null}
        </p>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {[
            e.sentAt ? t('estimates.detail.sentOn', { date: on(e.sentAt) }) : null,
            e.expiresOn && e.status !== 'Approved'
              ? t('estimates.detail.expires', { date: formatDateOnly(e.expiresOn) })
              : null,
            e.approvedAt ? t('estimates.detail.approvedOn', { date: on(e.approvedAt) }) : null,
            e.declinedAt ? t('estimates.detail.declinedOn', { date: on(e.declinedAt) }) : null,
            e.supersededAt
              ? t('estimates.detail.supersededOn', { date: on(e.supersededAt) })
              : null,
            // M2-09: everyone who can open the estimate sees that it became
            // work, and the job link above takes them to it.
            e.convertedAt ? t('estimates.detail.convertedOn', { date: on(e.convertedAt) }) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      {e.status === 'Draft' ? (
        // Keyed by id so a revision opened from here starts a fresh editor.
        <EstimateBuilder
          key={e.id}
          estimate={e}
          hourlyRate={hourlyRate}
          onSettledChange={onSettledChange}
        />
      ) : (
        <>
          {/* The answer first (M2-08) — once there is one, it is the news:
              the customer's signed record, or a line saying the office
              recorded it. Everyone who can open the estimate sees it; the API
              blanks the device for a technician. */}
          <CustomerAnswer estimate={e} timeZone={timeZone} />
          <EstimateView estimate={e} />
          <EstimateNotesView customerMessage={e.customerMessage} internalNotes={e.internalNotes} />
          {/* M2-48: what this revision went out with — the snapshot, never
              the shop's current terms. */}
          <DocumentTerms
            terms={e.terms?.terms}
            warrantyStatement={e.terms?.warrantyStatement}
            termsHeading={t('documentTerms.estimateTerms')}
            footnote={
              e.terms
                ? t('documentTerms.version', {
                    version: Number(e.terms.version),
                    date: on(e.terms.savedAt),
                  })
                : null
            }
          />
        </>
      )}

      {/* The customer's link (M2-07): the office only, and only while the API
          would hand one out — keyed by id, so a revision starts with no link shown. */}
      {isOffice && LINK_STATUSES.has(e.status) ? <CustomerLink key={e.id} estimate={e} /> : null}

      {isOffice && e.status !== 'Approved' && e.status !== 'Superseded' ? (
        <EstimateActs
          estimate={e}
          draftSettled={e.status !== 'Draft' || draftSettled}
          onAnnounce={setAnnouncement}
        />
      ) : null}

      {/* M2-09: the office's next act once the customer said yes. Hidden once
          converted — the header then says so and links the job. */}
      {isOffice && e.status === 'Approved' && e.convertedAt === null ? (
        <ConvertToJob key={e.id} estimate={e} />
      ) : null}

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </section>
  );
}
